
import { Enums } from '@bode-canada/core';
import Button from '@/ui-kit/components/button';
import { ButtonFillType } from '@/ui-kit/components/button/enums';
import ModalService from '@/ui-kit/components/modal/ModalService';
import { StyleColorSchemeType } from '@/ui-kit/styles/enums';
import { ToastMessage } from '@/ui-kit/components/toast/enums';
import routes from '@/constants/routes';
import { QueryParams } from '@/constants/QueryParams';
import { ListingPageEvent } from '@/constants/AnalyticEvents';
import { LISTING_ORIGIN_TYPES, LISTING_TRACK_EVENT_ORIGIN_TYPES } from '@/constants';
import { emailValidator } from '@/utils/validators';
import ToastService from '@/components/toast/toast-service';
import ListingOwnerContactsRequestInfoModal from './request-info-modal';
import ListingOwnerContactsUserRoleModal from './user-role-modal';
import ListingOwnerContactsHeader from './header';
import ListingBookViewing from '../book-viewing';
import BookViewingPayloadMapper from '../book-viewing/helpers/payloadMapper';
import MLSContact from './mls-contact';
import { OwnerContactsEvent, RequestInfoReason } from './enums';
import { UserRole } from '@/constants/user';

export default {
  name: 'ListingOwnerContacts',
  components: {
    Button,
    ListingOwnerContactsHeader,
    ListingBookViewing,
    MLSContact,
  },
  props: {
    id: {
      required: true,
      type: Number,
    },
    origin: {
      required: true,
      type: String,
    },
    owners: {
      required: true,
      type: Array,
    },
    isUserTitleHolder: {
      required: true,
      type: Boolean,
    },
    userId: {
      default: 0,
      type: Number,
    },
    userEmail: {
      default: '',
      type: String,
    },
    isUserRealtor: {
      default: false,
      type: Boolean,
    },
    additionalHolders: {
      type: Array,
      default: () => ([]),
    },
    requiresIdVerification: {
      type: Boolean,
      default: false,
    },
    addressState: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      StyleColorSchemeType,
      ButtonFillType,
      isBookViewingInProgress: false,
    };
  },
  computed: {
    isBodeListing() {
      return this.origin === Enums.ListingOriginEnum.BODE;
    },
    ownersWithEmail() {
      return this.owners.filter(({ email }) => emailValidator(email));
    },
    user() {
      return this.$store.state.user.userData;
    },
    trackEventAttributes() {
      return {
        listingId: this.id,
        listingType: LISTING_ORIGIN_TYPES[this.origin] || this.origin,
        email: this.user.email || '',
        authenticated: this.user.isAuthenticated,
      };
    },
    additionalTitleHolders() {
      const MAX_VISIBLE_TITLE_HOLDERS = 2;

      return this.additionalHolders.slice(0, MAX_VISIBLE_TITLE_HOLDERS);
    },
    isUserAuthenticated() {
      return this.user.isAuthenticated;
    },
    isUserVerified() {
      return this.user.isVerified;
    },
    loginPath() {
      return {
        path: routes.login,
        query: {
          [QueryParams.Referrer]: this.$route.fullPath,
        },
      };
    },
  },
  methods: {
    getMessageSellerUrl({ userId }) {
      const queryString = `${QueryParams.ListingId}=${this.id}&${QueryParams.UserId}=${userId}`;

      return `${routes.messages}?${queryString}`;
    },
    handleSellerProfileLinkClick() {
      if (this.isBodeListing) {
        this.$track(ListingPageEvent.BodeSellerProfileClicked, {
          listingId: this.id,
          userId: this.userId,
        });
      }
    },
    handleMessageSellerClick() {
      this.$track(ListingPageEvent.MessageSentToSeller, {
        ...this.trackEventAttributes,
        category: 'Listing Engagement',
        eventlabel: 'Listing Engagement - Message Sent',
      });
    },
    handleMakeOfferClick() {
      if (this.ownersWithEmail.length) {
        this.makeOffer();
      } else {
        this.requestInfo(RequestInfoReason.MakeOffer);
      }
    },
    handleBookViewingSubmit(request) {
      if (!this.isUserAuthenticated) {
        return ToastService.openNeutral(ToastMessage.BookViewingUnauthenticated);
      }

      if (!this.isUserVerified && this.requiresIdVerification) {
        return ToastService.openNeutral(ToastMessage.BookViewingUnverified);
      }

      this.trackBookViewing();
      return this.submitBookViewing(BookViewingPayloadMapper.mapBodePayload(request));
    },
    submitBookViewing(request) {
      ToastService.openNeutral(ToastMessage.BookViewing);

      this.$emit(OwnerContactsEvent.BookViewing, request);
    },
    async makeOffer() {
      await this.askUserRole();
    },
    async requestInfo(reason) {
      const trackEvent = this.getTrackEventWithOrigin(ListingPageEvent.RequestInfoClicked);
      const email = await ModalService.open({
        component: ListingOwnerContactsRequestInfoModal,
        data: { email: this.userEmail },
      });

      if (email) {
        this.$track(trackEvent, this.trackEventAttributes);
        this.$emit(OwnerContactsEvent.RequestInfo, { email, reason });
      }
    },
    async askUserRole() {
      const userRole = await ModalService.open({ component: ListingOwnerContactsUserRoleModal });

      if (userRole) {
        this.$router.push({
          path: routes.offers,
          query: { listingId: this.id, isAgentOffer: userRole !== UserRole.Buyer },
        });
      } else {
        this.$track(ListingPageEvent.CancelOfferClicked, this.trackEventAttributes);
      }

      return null;
    },
    trackBookViewing() {
      const event = this.user.isAuthenticated
        ? ListingPageEvent.BookViewingClickedAuthenticated
        : ListingPageEvent.BookViewingClickedUnauthenticated;
      const trackEvent = this.getTrackEventWithOrigin(event);

      this.$track(trackEvent, {
        ...this.trackEventAttributes,
        category: 'Book A Viewing',
        eventlabel: 'Book A Viewing - Started',
      });
    },
    trackMakeOfferClick(isRealtor) {
      const roleEvent = isRealtor
        ? ListingPageEvent.AgentOfferStarted
        : ListingPageEvent.BuyerOfferStarted;
      const event = this.isBodeListing ? roleEvent : ListingPageEvent.MLSOfferStarted;

      this.$track(event, {
        ...this.trackEventAttributes,
        category: 'Offer Submission',
        eventlabel: 'Offer Submission - Started',
        userId: this.userId,
        email: this.userEmail,
      });
    },
    getTrackEventWithOrigin(trackEvent) {
      const listingTrackEventOrigin = this.isBodeListing
        ? LISTING_TRACK_EVENT_ORIGIN_TYPES.Bode
        : LISTING_TRACK_EVENT_ORIGIN_TYPES.MLS;

      return `${listingTrackEventOrigin}_${trackEvent}`;
    },
    trackMLSExternalLinkClick() {
      this.$track(ListingPageEvent.MLSContactLinkClicked, {
        ...this.trackEventAttributes,
      });
    },
  },
};
